import { Injectable, Signal, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { WorkspacePreferencesClient } from '@neworg-net/amsuite-core/workspace';
import { AmsuitePreferences, EffectivePreferencesDto, UserPreferencesDto } from '../models';

/**
 * Facade tipizzato sul client preferenze Workspace condiviso
 * (`@neworg-net/amsuite-core/workspace`). HTTP + cache rawUser + read-modify-write
 * vivono nella lib; qui resta la view tipizzata di amsuite (`AmsuitePreferences`,
 * solo campi shell) e la firma storica `putUser(prefs, appId?)` + il `rawUser`
 * esposto come Signal (consumato reattivamente dai componenti).
 *
 * Scope di default = appId dell'app corrente (risolto dalla lib dal ConfigService).
 * Base URL Workspace + Bearer + X-Party-Owner-Id gestiti dalla lib (host registry +
 * interceptor di `@neworg-net/amsuite-core/auth`).
 */
@Injectable({ providedIn: 'root' })
export class WorkspacePreferencesService {
  private readonly client = inject<WorkspacePreferencesClient<AmsuitePreferences>>(WorkspacePreferencesClient);

  /**
   * Ultimo delta user noto dello scope di default (app amsuite), come Signal.
   * Mirror del rawUser della lib: la lib lo aggiorna su getUser/putUser; qui
   * riusiamo direttamente il signal interno del client (stesso valore reattivo).
   */
  readonly rawUser: Signal<AmsuitePreferences> = this.client.rawUserSignal();

  getEffective(appId?: string): Observable<EffectivePreferencesDto> {
    return this.client.getEffective(appId);
  }

  getUser(appId?: string): Observable<UserPreferencesDto> {
    return this.client.getUser(appId);
  }

  getTenant(appId?: string): Observable<UserPreferencesDto> {
    return this.client.getTenant(appId);
  }

  getSystem(appId?: string): Observable<UserPreferencesDto> {
    return this.client.getSystem(appId);
  }

  putUser(prefs: AmsuitePreferences, appId?: string): Observable<UserPreferencesDto> {
    return this.client.putUser(prefs, appId);
  }

  putTenant(prefs: AmsuitePreferences, appId?: string): Observable<UserPreferencesDto> {
    return this.client.putTenant(prefs, appId);
  }

  putSystem(prefs: AmsuitePreferences, appId?: string): Observable<UserPreferencesDto> {
    return this.client.putSystem(prefs, appId);
  }

  parse(json: string | null | undefined): AmsuitePreferences {
    return this.client.parse(json);
  }

  stringify(prefs: AmsuitePreferences): string {
    return this.client.stringify(prefs);
  }
}
